import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Modal from "@shared/v2/Modal";
import TextComboInput from "@shared/v2/TextComboInput";
import Button from "@shared/v2/Button";

const EditAddCNAMModal = ({
  showCnam,
  onClose,
  onSave,
  error,
  onErrorMsgAddCnam,
  title,
  openDeleteCnamModalHandlr,
}) => {
  const [cnam, setCnam] = useState("");
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const onCloseHandlr = () => {
    setCnam("");
    onClose();
    onErrorMsgAddCnam(null);
  };

  useEffect(() => {
    if (showCnam?.displayName) setCnam(showCnam?.displayName);
  }, [showCnam?.displayName]);

  return (
    <Modal
      className="tw-flex tw-items-center tw-justify-center"
      show={Boolean(showCnam)}
      onHide={onCloseHandlr}
      contentClassName="tw-max-w-[616px]"
    >
      <Modal.Header
        className="tw-relative tw-mb-32px"
        title={title}
        description={
          <span className="tw-text-14d tw-text-gray-50">
            This is the name that shows up when you call someone from Brivity. Setting a Caller ID Name helps
            people recognize who&#39;s calling and makes you less likely to be marked as spam. <b>Note:</b>{" "}
            Caller ID Names can take up to 72 hours to approve and update across all carriers.
          </span>
        }
        closeButton
      />
      <Modal.Body className="tw-flex tw-flex-col tw-gap-24px tw-p-8px tw-mb-32px">
        <TextComboInput
          label="Caller ID Name"
          value={cnam}
          onChange={(e) => {
            // eslint-disable-next-line prefer-destructuring
            const value = e.target.value;

            if (value.length <= 15) {
              setCnam(value);
              onErrorMsgAddCnam(null);
            }
          }}
          helperText={error ? null : `${cnam.length} / 15 characters`}
          error={
            error
              ? "Please make sure you choose a Caller ID Name that begins with a letter and contains only letters, numbers, periods, commas, and spaces."
              : null
          }
          isRequired
        />

        <div className="tw-text-14d">
          <div className="tw-font-semibold tw-mb-8px">Requirements</div>
          <ul className="tw-pl-25px tw-mb-0">
            <li>Maximum of 15 characters</li>
            <li>Must begin with a letter</li>
            <li>Can only contain letters, numbers, periods, commas and spaces</li>
            <li>Must be a unique name or value, cannot be a generic value such as a City or State</li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-items-center">
        {openDeleteCnamModalHandlr ? (
          <Button
            size="medium"
            schema="warning"
            onClick={() =>
              openDeleteCnamModalHandlr({ displayName: cnam, userId: showCnam?.userId, id: showCnam?.id })
            }
            className="!tw-border-transparent hover:!tw-bg-transparent"
          >
            Delete caller id name
          </Button>
        ) : (
          <Button size="medium" schema="tertiary" onClick={onCloseHandlr}>
            Close
          </Button>
        )}
        <Button
          size="medium"
          onClick={async () => {
            setIsSaveLoading(true);
            await onSave({ displayName: cnam, userId: showCnam?.userId, id: showCnam?.id });
            setIsSaveLoading(false);
            onCloseHandlr();
          }}
          disabled={!/^[A-Za-z]/.test(cnam) || !/^[A-Za-z][A-Za-z0-9., ]*$/.test(cnam) || error}
          isLoading={isSaveLoading}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAddCNAMModal;

EditAddCNAMModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onErrorMsgAddCnam: PropTypes.func.isRequired,
  error: PropTypes.bool,
  showCnam: PropTypes.shape({
    userId: PropTypes.number,
    id: PropTypes.number,
    displayName: PropTypes.string,
    action: PropTypes.string,
  }),
  title: PropTypes.string,
  openDeleteCnamModalHandlr: PropTypes.func,
};

EditAddCNAMModal.defaultProps = {
  showCnam: null,
  error: null,
  openDeleteCnamModalHandlr: null,
  title: "Add Caller ID Name",
};
